/**
 * Shared helpers for the plan orchestration hook family.
 *
 * Extracted to avoid duplicating isAbortError, toErrMsg, and captureDebugPrompts
 * across usePlanOrchestration, usePlanWizardHandlers, usePlanQualityGate, and
 * usePlanPhaseExecution.
 */
import { z } from 'zod';
import { useDebugStore } from '@/stores/ai/debug-store';
import type { PlanLLMDebugCall } from '@/types/plan';

/** Returns true if err is a DOM/fetch AbortError so callers can skip error reporting. */
export function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

/** Safely extracts a message string from an unknown catch value. */
export function toErrMsg(err: unknown, fallback = ''): string {
  return err instanceof Error ? (err.message || fallback) : fallback;
}

/**
 * Zod schema for a single LLM debug call as returned by the API.
 * Fields beyond stage/targetId are optional because different response shapes
 * include different subsets.
 */
const LLMDebugCallSchema = z.object({
  stage: z.string().optional(),
  targetId: z.string().optional(),
  systemPromptName: z.string().optional().default(''),
  systemPrompt: z.string().optional().default(''),
  userPrompt: z.string().optional().default(''),
  rawResponse: z.string().optional(),
}).passthrough();

/** Safely narrow an unknown value to Record<string, unknown> or return undefined. */
export function asRecord(val: unknown): Record<string, unknown> | undefined {
  return (val && typeof val === 'object' && !Array.isArray(val))
    ? val as Record<string, unknown>
    : undefined;
}

/** Safely narrow an unknown value to an array or return an empty array. */
export function asRecordArray(val: unknown): Record<string, unknown>[] {
  if (!Array.isArray(val)) return [];
  return val.filter((item): item is Record<string, unknown> =>
    item != null && typeof item === 'object' && !Array.isArray(item),
  );
}

/** Parse a debug payload into a PlanLLMDebugCall, filling in stage/targetId defaults. */
function parseDebugCall(
  raw: unknown,
  defaultStage: string,
  targetId?: string,
): PlanLLMDebugCall | null {
  const parsed = LLMDebugCallSchema.safeParse(raw);
  if (!parsed.success) return null;
  return {
    ...parsed.data,
    stage: parsed.data.stage || defaultStage,
    targetId: targetId ?? parsed.data.targetId,
    systemPromptName: parsed.data.systemPromptName,
    systemPrompt: parsed.data.systemPrompt,
    userPrompt: parsed.data.userPrompt,
    rawResponse: parsed.data.rawResponse,
  };
}

/**
 * Extract _debug LLM call data from API responses and record in debug store.
 *
 * Handles three response shapes:
 *   Shape 1: { data: { _debug: { llmCalls: [...] } } }
 *   Shape 2: { data: { _debug: { systemPromptName, systemPrompt, ... } } }
 *   Shape 3: { data: { auditResult: { _debug: { ... } } } }
 */
export function captureDebugPrompts(
  response: unknown,
  stage: string,
  targetId?: string,
): void {
  const debug = useDebugStore;
  if (!debug.getState().isEnabled) return;
  const calls: PlanLLMDebugCall[] = [];

  const r = asRecord(response);
  if (!r) return;

  const data = asRecord(r.data);
  const debugPayload = asRecord(data?._debug ?? r._debug);

  // Shape 1: { _debug: { llmCalls: [...] } }
  if (debugPayload?.llmCalls && Array.isArray(debugPayload.llmCalls)) {
    for (const rawCall of debugPayload.llmCalls) {
      const call = parseDebugCall(rawCall, stage, targetId);
      if (call) calls.push(call);
    }
  }

  // Shape 2: { _debug: { systemPromptName, systemPrompt, ... } }
  if (debugPayload?.systemPromptName && !debugPayload.llmCalls) {
    const call = parseDebugCall(debugPayload, stage, targetId);
    if (call) calls.push(call);
  }

  // Shape 3: { data: { auditResult: { _debug: { ... } } } }
  const auditResult = asRecord(data?.auditResult);
  const auditDebug = asRecord(auditResult?._debug);
  if (auditDebug?.systemPromptName) {
    const call = parseDebugCall(auditDebug, stage, targetId);
    if (call) calls.push(call);
  }

  if (calls.length) {
    debug.getState().recordPlanStagePrompts(calls);
  }
}
